/* eslint-disable @typescript-eslint/return-await */
import _get from 'lodash/get';
import logger from 'src/logger';
import {
  loaderIndicatorType,
  makeLoaderIndicator,
  makeVideoButton,
  videoButtonType,
} from 'src/sdk-components';
import ThreeInstance from 'src/sdk-components/ThreeInstance';
import SceneHTML from 'src/service/scene/scene-nodes/SceneHTML';
import ScenePolygon from 'src/service/scene/scene-nodes/ScenePolygon';
//import { CAMERA_HEIGHT, SHADOW_OFFSET } from 'src/service/scene/scene-nodes/SceneShadow/data';
import { SDKConfig } from 'src/service/types';
import { MpSdk } from 'src/types';
import { DeviceManager } from 'src/utils';
import { SceneSettings } from 'src/_models';
import { FRFloor } from 'src/_models/FRFloor';
import { makeThreeInstance, threeInstanceComponentType } from 'src/_models/ThreeInstance.component';
import { SceneInitEventsTypes } from './enums';
import {
  SceneAvatar,
  SceneImage,
  SceneLoader,
  SceneObject,
  SceneShadow,
  SceneVideo,
  TransformControls,
  ScenePatch,
} from './scene-nodes';
import {
  AvatarConfigType,
  PolygonConfigType,
  TCoordinates,
  TSceneInitEvent,
  TSceneNode,
  VideoConfigType,
} from './types';
import { CAMERA_HEIGHT, SHADOW_OFFSET } from 'src/service/scene/scene-nodes/SceneShadow/data';
import { isMediaType } from 'src/utils/matterNodesHelpers';
import { degToRad } from 'three/src/math/MathUtils';
import SceneController from 'src/service/scene/scene-nodes/SceneController';
import {
  controllerButtonType,
  makeControllerButton,
} from 'src/sdk-components/ControllerButtonComponent';
import SceneBarrier from 'src/service/scene/scene-nodes/SceneBarrier';
import SceneCustomSweep from 'src/service/scene/scene-nodes/SceneCustomSweep';
import SceneIcon from 'src/service/scene/scene-nodes/SceneIcon';
import SceneTrigger from 'src/service/scene/scene-nodes/SceneTrigger';
import {
  makeShowcaseContext,
  showcaseContextComponentType,
} from 'src/sdk-components/ShowcaseContext/ShowcaseContext';
import ShowcaseContextInstance from 'src/sdk-components/ShowcaseContextInstance';
import SceneTeleport from 'src/service/scene/scene-nodes/SceneTeleport';
import ScenePathPoint from 'src/service/scene/scene-nodes/ScenePathPoint';

export { SceneInitEventsTypes } from './enums';

export class NodeFactory {
  private readonly mpSDK: any;

  public sceneObject: SceneObject;

  public sceneShadow: SceneShadow;

  public sceneImage: SceneImage;

  public sceneHTML: SceneHTML;

  public sceneVideo: SceneVideo;

  public scenePolygon: ScenePolygon;

  public sceneAvatar: SceneAvatar;

  public scenePatch: ScenePatch;

  public sceneController: any;

  public sceneBarrier: SceneBarrier;

  public sceneCustomSweep: SceneCustomSweep;

  public sceneTeleport: SceneTeleport;

  public scenePathPoint: ScenePathPoint;

  public sceneIcon: SceneIcon;

  public sceneTrigger: SceneTrigger;

  public transformControls: TransformControls;

  private floors: FRFloor[] = [];

  public floor: FRFloor;

  private lastCreatedNodeType: TSceneInitEvent;

  private modelNodeToAdd: any;

  private modelNodeToAddSize: any;

  private shadowNode: any;

  private inputComponent: any;

  public shadowNodeComponent: any;

  public loaderNode: any;

  public lightsNode: any;

  public pointLightComponent: any;

  public ambientLightComponent: any;

  public directionalLightComponent: any;

  public context: any;

  private handleIframeClick() {}

  public pose: MpSdk.Camera.Pose;

  // TODO: pass iframeWindow to VS, Media and Polygons
  // @ts-ignore
  constructor(
    mpSDK: MpSdk,
    private config: SDKConfig,
    private iframeWindow,
  ) {
    this.mpSDK = mpSDK;
  }

  async init() {
    await this.configureThreeInstance();
    this.context = await this.configureShowcaseContextInstance();
    this.mpSDK.Scene.register(loaderIndicatorType, makeLoaderIndicator);
    this.mpSDK.Scene.register(videoButtonType, makeVideoButton);
    this.mpSDK.Scene.register(controllerButtonType, makeControllerButton);
    this.transformControls = new TransformControls(this.mpSDK);
    this.transformControls.create();
    this.sceneShadow = new SceneShadow(this.mpSDK);
    this.sceneShadow.init();
    this.sceneObject = new SceneObject(this.mpSDK, this.config, this.transformControls);
    this.sceneObject.init();
    this.sceneImage = new SceneImage(this.mpSDK, this.config, this.transformControls);
    this.sceneImage.init();
    this.sceneHTML = new SceneHTML(this.mpSDK, this.config, this.transformControls);
    this.sceneHTML.init();
    this.sceneVideo = new SceneVideo(this.mpSDK, this.config, this.transformControls);
    this.sceneVideo.init();
    this.scenePolygon = new ScenePolygon(this.mpSDK, this.transformControls);
    this.scenePolygon.init();
    this.sceneAvatar = new SceneAvatar(this.mpSDK);
    this.sceneAvatar.init();
    this.scenePatch = new ScenePatch(this.mpSDK);
    this.scenePatch.init();
    this.sceneController = new SceneController(this.mpSDK, this.config, this.transformControls);
    this.sceneController.init();
    this.sceneBarrier = new SceneBarrier(this.mpSDK, this.config, this.transformControls);
    this.sceneBarrier.init();
    this.sceneCustomSweep = new SceneCustomSweep(this.mpSDK, this.transformControls);
    this.sceneCustomSweep.init();
    this.sceneIcon = new SceneIcon(this.mpSDK, this.transformControls);
    this.sceneIcon.init();
    this.sceneTrigger = new SceneTrigger(this.mpSDK);
    this.sceneTrigger.init();
    this.sceneTeleport = new SceneTeleport(this.mpSDK, this.config, this.transformControls);
    this.sceneTeleport.init();
    this.scenePathPoint = new ScenePathPoint(this.mpSDK, this.transformControls);
    this.scenePathPoint.init();
    if (this.config?.settings) {
      await this.configureScene({ settings: this.config.settings });
    }

    this.mpSDK.Sweep.data.subscribe({
      onCollectionUpdated: (collection: any) => {
        const transformToUUID = (id: string) =>
          id.slice(0, 8) +
          '-' +
          id.slice(8, 12) +
          '-' +
          id.slice(12, 16) +
          '-' +
          id.slice(16, 20) +
          '-' +
          id.slice(20);
        // eslint-disable-next-line @typescript-eslint/ban-types
        const sweeps = Object.values(collection).map((item: Object) => ({
          ...item,
          // eslint-disable-next-line @typescript-eslint/dot-notation
          sweep_uuid: transformToUUID(item['id']),
          floor_index: Number(_get(item, 'floorInfo.id')),
          floor_position: _get(item, 'position'),
        }));

        this.initFloors(sweeps);
      },
    });

    this.mpSDK.Camera.pose.subscribe((p: MpSdk.Camera.Pose) => {
      this.pose = { ...p };

      if (this.sceneCustomSweep.assets.size) {
        this.sceneCustomSweep.assets.forEach((node) => {
          node[1].root.rotation.y = degToRad(p.rotation.y) + Math.PI;
        });
      }
    });

    this.mpSDK.Sweep.current.subscribe(async (currentSweep) => {
      const THREE = await ThreeInstance.get();
      // Change to the current sweep has occurred.
      if (currentSweep.sid !== '') {
        if (this.shadowNode) {
          this.shadowNode.obj3D.position.set(
            currentSweep.position.x,
            Math.abs(currentSweep.position.y - CAMERA_HEIGHT + SHADOW_OFFSET), // it disabled shadow of vsObjects if 0
            currentSweep.position.z,
          );
        }
        const position = new THREE.Vector3(
          currentSweep.position.x,
          currentSweep.position.y,
          currentSweep.position.z,
        );
        if (this.sceneVideo.assets.length) {
          this.sceneVideo.assets.forEach(([videoAsset, node]) => {
            if (node?.[0] && videoAsset.autoRotate) {
              node[0].obj3D.lookAt(position);
            }
          });
        }
        if (this.sceneImage.assets.length) {
          this.sceneImage.assets.forEach(([imageAsset, node]) => {
            if (node?.[0] && imageAsset.autoRotate) {
              node[0].obj3D.lookAt(position);
            }
          });
        }
        this.sceneTeleport.assets.forEach(([teleportAsset, node]) => {
          if (node) {
            node.pivot.lookAt(position.x, teleportAsset.obj3D.position.y, position.z);
          }
        });
      }
    });

    this.mpSDK.Mode.current.subscribe((mode) => {
      // turn of visibility for MP panorama sweeps
      if (this.shadowNode) {
        this.shadowNode.obj3D.visible = mode !== 'mode.outside';
      }
    });

    // TODO: move stuff from this.mpSDK.Camera.pose.subscribe()
    // this.showcase.Sweep.current.subscribe((currentSweep: { sid: string }) => {
    // });

    // TODO: hide for public
    this.mpSDK.Pointer.intersection.subscribe(this.handleModelIntersection);

    // TODO: hide for public
    // 'mp.input' needed to block user navigating events in the tour when the adding process is enabled
    const [sceneObject] = await this.mpSDK.Scene.createObjects(1);
    const inputNode = sceneObject.addNode();
    this.inputComponent = inputNode.addComponent('mp.input');
    inputNode.start();
  }

  async configureThreeInstance() {
    try {
      await this.mpSDK.Scene.register(threeInstanceComponentType, makeThreeInstance);

      const node = await this.mpSDK.Scene.createNode();
      const threeInstanceComponent = node.addComponent(threeInstanceComponentType);
      node.start();

      //this.THREE = threeInstanceComponent.get();

      ThreeInstance.set(threeInstanceComponent.get());
      if (ThreeInstance.get() == null) {
        console.error('Could not get hook on three');
      }
    } catch (e) {
      logger.error(e);
    }
  }

  async configureShowcaseContextInstance() {
    try {
      await this.mpSDK.Scene.register(showcaseContextComponentType, makeShowcaseContext);

      const node = await this.mpSDK.Scene.createNode();
      const showcaseContextInstanceComponent = node.addComponent(showcaseContextComponentType);
      node.start();

      ShowcaseContextInstance.set(showcaseContextInstanceComponent.get());
      if (ShowcaseContextInstance.get() == null) {
        console.error('Could not get hook on showcase context');
      }
      return showcaseContextInstanceComponent.get();
    } catch (e) {
      logger.error(e);
    }
  }

  async createSceneLoader() {
    const sceneLoader = new SceneLoader(this.mpSDK, this.config.logoUrl);
    return sceneLoader?.create();
  }

  async create(
    type: TSceneInitEvent,
    data: any,
    config?: AvatarConfigType | PolygonConfigType | VideoConfigType | any,
    enableCollider?: boolean,
  ): Promise<TSceneNode | undefined> {
    if (type === SceneInitEventsTypes.Video && !data.isShowPreLoader) {
      return this.sceneVideo.create(data, config);
    }
    if (type === SceneInitEventsTypes.Object && !data.isShowPreLoader) {
      return this.sceneObject.create(data, enableCollider);
    }
    const loaderNode = await this.createSceneLoader();
    if (loaderNode?.[0]) {
      loaderNode[0].obj3D.position.set(data.positionX, data.positionY, data.positionZ);
    }
    this.lastCreatedNodeType = type;
    let result;
    switch (type) {
      case SceneInitEventsTypes.Avatar: {
        result = await new SceneAvatar(this.mpSDK).create(data, config);
        break;
      }
      case SceneInitEventsTypes.Image: {
        result = await this.sceneImage.create(data);
        break;
      }
      case SceneInitEventsTypes.HTML: {
        result = await this.sceneHTML.create(data);
        break;
      }
      case SceneInitEventsTypes.Video: {
        result = await this.sceneVideo.create(data, config);
        break;
      }
      case SceneInitEventsTypes.Object: {
        result = await this.sceneObject.create(data, enableCollider);
        break;
      }
      case SceneInitEventsTypes.Polygon: {
        result = await this.scenePolygon.create(data);
        break;
      }
      case SceneInitEventsTypes.Controller: {
        result = await this.sceneController.create(data);
        break;
      }
      case SceneInitEventsTypes.Patch: {
        result = await this.scenePatch.create(data);
        break;
      }
      case SceneInitEventsTypes.Barrier: {
        result = await this.sceneBarrier.create(data);
        break;
      }
      case SceneInitEventsTypes.CustomSweep: {
        result = await this.sceneCustomSweep.create(data);
        break;
      }
      case SceneInitEventsTypes.Icon: {
        result = await this.sceneIcon.create(data);
        break;
      }
      case SceneInitEventsTypes.Trigger: {
        result = await this.sceneTrigger.create(data);
        break;
      }
      case SceneInitEventsTypes.Teleport: {
        result = await this.sceneTeleport.create(data);
        break;
      }
      case SceneInitEventsTypes.PathPoint: {
        result = await this.scenePathPoint.create(data);
        break;
      }
    }
    if (loaderNode?.[0]) {
      loaderNode[0].stop();
    }
    return result;
  }

  async remove(type: TSceneInitEvent, data: any): Promise<void> {
    switch (type) {
      case SceneInitEventsTypes.Image: {
        await this.sceneImage.remove(data);
        break;
      }
      case SceneInitEventsTypes.Video: {
        await this.sceneVideo.remove(data);
        break;
      }
      case SceneInitEventsTypes.Object: {
        await this.sceneObject.remove(data);
        break;
      }
      case SceneInitEventsTypes.Polygon: {
        await this.scenePolygon.remove(data);
        break;
      }
      case SceneInitEventsTypes.HTML: {
        await this.sceneHTML.remove(data);
        break;
      }
      case SceneInitEventsTypes.Barrier: {
        await this.sceneBarrier.remove(data);
        break;
      }
      case SceneInitEventsTypes.CustomSweep: {
        await this.sceneCustomSweep.remove(data);
        break;
      }
      case SceneInitEventsTypes.Icon: {
        await this.sceneIcon.remove(data);
        break;
      }
      case SceneInitEventsTypes.Trigger: {
        await this.sceneTrigger.remove(data);
        break;
      }
      case SceneInitEventsTypes.Teleport: {
        await this.sceneTeleport.remove(data);
        break;
      }
      case SceneInitEventsTypes.PathPoint: {
        await this.scenePathPoint.remove(data);
        break;
      }
    }
  }

  async configureScene({
    settings,
    cb,
    onShadowEnabled,
    onShadowNodeCreated,
  }: {
    settings: SceneSettings | undefined;
    onShadowEnabled?: (data: any) => void;
    onShadowNodeCreated?: (data: any) => void;
    cb?: () => void;
  }) {
    await this.mpSDK.Scene.configure((_renderer, three, effectComposer) => {
      if (effectComposer) {
        if (window.devicePixelRatio >= 2) effectComposer.setPixelRatio(2);
        else effectComposer.setPixelRatio(1);
        const outlinePass = new three.OutlinePass(
          new three.Vector2(window.innerWidth, window.innerHeight),
          effectComposer.passes[0].scene,
          effectComposer.passes[0].camera,
        );

        const color = new three.Color();
        color.copy(outlinePass.visibleEdgeColor);
        outlinePass.hiddenEdgeColor = color;

        if (!DeviceManager.isMobile()) {
          effectComposer.addPass(outlinePass);
          if (effectComposer.renderer.xr.enabled) {
            effectComposer.renderer.xr.addEventListener('sessionstart', () => {
              if (effectComposer.passes.length > 1)
                effectComposer.passes.forEach((pass, index) => {
                  if (index > 0) effectComposer.removePass(pass);
                });
            });
            effectComposer.renderer.xr.addEventListener('sessionend', () => {
              if (effectComposer.passes.length === 1) effectComposer.addPass(outlinePass);
            });
          }
        }
      } else {
        if (window.devicePixelRatio >= 2) _renderer.setPixelRatio(2);
        else _renderer.setPixelRatio(1);
      }
    });
    if (DeviceManager.isMobile()) {
      if (!settings) return;
      const THREE = await ThreeInstance.get();
      console.log('adding lights', settings);
      // Hardcode only specific values for lights because
      // if physicallyCorrectLights is false, it needs different values
      // Add some lights to the scene, otherwise, models will be black.
      // https://matterport.github.io/showcase-sdk/sdkbundle_components_ambientlight.html
      await this.mpSDK.Scene.configure((renderer: any, _three: any) => {
        renderer.antialias = false;
        renderer.useLegacyLights = false;
      });
      this.lightsNode = await this.mpSDK.Scene.createNode();
      this.pointLightComponent = this.lightsNode.addComponent('mp.pointLight', {
        position: {
          x: settings.lightAngle ? 10 : 0,
          y: settings.lightHeight,
          z: 0,
        },
        intensity: settings.pointLight,
        castShadow: false,
      });
      this.ambientLightComponent = this.lightsNode.addComponent('mp.ambientLight', {
        position: {
          x: 0,
          y: 0,
          z: 0,
        },
        intensity: settings.ambientLight,
      });
      this.directionalLightComponent = this.lightsNode.addComponent('mp.directionalLight', {
        enabled: true,
        color: {
          r: 1,
          g: 1,
          b: 1,
        },
        intensity: settings.directionalLightStrength,
        position: {
          x: 0,
          y: 1,
          z: 0,
        },
        target: {
          x: settings.directionalLightAngleX,
          y: 0,
          z: settings.directionalLightAngleY,
        },
        debug: false,
      });
      this.lightsNode.start();

      this.lightsNode.obj3D.rotation.set(
        0,
        settings.lightAngle ? THREE.MathUtils.degToRad(settings.lightAngle) : 0,
        0,
      );
    } else {
      if (!settings) return;
      console.log('adding lights', settings);
      // TODO: use threejs from MP SDK
      const THREE = await ThreeInstance.get();
      const hasFloorRendering = !this.floors.some((i) => !i.active) || this.floors?.length === 1;
      const isShadowVisible = settings.shadowIntensity > 0;
      // const isShadowEnabled = hasFloorRendering && this.sceneObject?.assets.size > 0 && isShadowVisible;
      const isShadowEnabled = hasFloorRendering && isShadowVisible; //TODO: disabled for now
      if (onShadowEnabled) onShadowEnabled(isShadowEnabled);

      // const pixelRatio = window.devicePixelRatio;
      // let AA = false;
      // if (pixelRatio > 1) {
      //   AA = false;
      // }

      await this.mpSDK.Scene.configure((renderer: any, three: any) => {
        renderer.powerPreference = 'high-performance';
        renderer.antialias = true;
        renderer.shadowMap.enabled = true;
        renderer.useLegacyLights = false;
        renderer.shadowMap.bias = 0.0001;
        renderer.shadowMap.type = three.PCFSoftShadowMap;
      });
      // Add some lights to the scene, otherwise, models will be black.
      // https://matterport.github.io/showcase-sdk/sdkbundle_components_ambientlight.html
      this.lightsNode = await this.mpSDK.Scene.createNode();
      this.pointLightComponent = this.lightsNode.addComponent('mp.pointLight', {
        position: {
          x: settings.lightAngle ? 10 : 0,
          y: settings.lightHeight,
          z: 0,
        },
        intensity: settings.pointLight,
        castShadow: isShadowEnabled,
      });
      this.ambientLightComponent = this.lightsNode.addComponent('mp.ambientLight', {
        position: {
          x: 0,
          y: 0,
          z: 0,
        },
        intensity: settings.ambientLight,
      });

      this.directionalLightComponent = this.lightsNode.addComponent('mp.directionalLight', {
        enabled: true,
        color: {
          r: 1,
          g: 1,
          b: 1,
        },
        intensity: settings.directionalLightStrength,
        position: {
          x: 0,
          y: 1,
          z: 0,
        },
        target: {
          x: settings.directionalLightAngleX,
          y: 0,
          z: settings.directionalLightAngleY,
        },
        debug: false,
      });
      this.lightsNode.start();

      this.lightsNode.obj3D.rotation.set(
        0,
        settings.lightAngle ? THREE.MathUtils.degToRad(settings.lightAngle) : 0,
        0,
      );

      // enable shadows only if VS objects found and no disabled floor for floor rendering is found
      this.pointLightComponent.light.angle = Math.PI * 0.2;
      this.pointLightComponent.light.shadow.bias = -0.000222;
      this.pointLightComponent.light.shadow.radius = 3;
      this.pointLightComponent.light.shadow.mapSize.width = 512; // default is 512
      this.pointLightComponent.light.shadow.mapSize.height = 512; // default is 512

      // Add shadow plane, shadows aren't available for the room model
      const [modelNode, component] = await this.sceneShadow.create(settings);
      this.shadowNode = modelNode;
      this.shadowNodeComponent = component;
      this.shadowNodeComponent.outputs.collider = null;
      if (onShadowNodeCreated) onShadowNodeCreated(modelNode);
      //if(onShadowNodeCreated) onShadowNodeCreated(null)
    }
    if (cb) {
      cb();
    }
  }

  startAddingProcess = async (): Promise<
    (node: any[], cb: (position: TCoordinates, rotation: TCoordinates) => void) => void
  > => {
    this.loaderNode = await this.createSceneLoader();
    return (node: any[], cb: (position: TCoordinates, rotation: TCoordinates) => void) =>
      this.addingProcess(node, cb);
  };

  /**
   * Attention! To work, the component must have the objectRoot property in his outputs.
   * */
  addingProcess = async (
    node: any[],
    cb: (position: TCoordinates, rotation: TCoordinates) => void,
  ) => {
    const THREE = await ThreeInstance.get();
    this.handleIframeClick = () => {
      this.iframeWindow.removeEventListener('click', this.handleIframeClick);
      this.inputComponent.inputs.userNavigationEnabled = true;
      this.inputComponent.inputs.eventsEnabled = false;
      this.inputComponent.inputs.unfiltered = true;

      node[0].obj3D.position.set(
        this.modelNodeToAdd[0].position.x,
        this.modelNodeToAdd[0].position.y,
        this.modelNodeToAdd[0].position.z,
      );
      node[0].obj3D.rotation.set(
        this.modelNodeToAdd[0].rotation.x,
        this.modelNodeToAdd[0].rotation.y,
        this.modelNodeToAdd[0].rotation.z,
      );
      node[0].obj3D.visible = true;

      // this.setNodeEditView(false, node[0]);
      node[0].obj3D.parent.remove(this.modelNodeToAdd[0]);
      this.modelNodeToAdd = null;
      this.modelNodeToAddSize = null;
      this.loaderNode = null;
      // cleaning up the iframe click handler
      this.handleIframeClick = () => {};
      const r = node[0].obj3D.rotation;
      cb(node[0].obj3D.position, { x: r._x, y: r._y, z: r._z });
    };

    try {
      // stopping the loader node
      if (this.loaderNode?.[0]) {
        this.loaderNode[0].stop();
      }
      if (!node?.length) return;

      // cloning the node to show as a edit view without cursor detection
      this.modelNodeToAdd = [node[0].obj3D.clone(), node[1]];

      // disabling the user interaction events for the tour
      this.inputComponent.inputs.userNavigationEnabled = false;
      this.inputComponent.inputs.eventsEnabled = true;
      this.inputComponent.inputs.unfiltered = false;

      // making invisible the original node
      node[0].obj3D.visible = false;

      // calculating the asset size
      const box = new THREE.Box3().setFromObject(this.modelNodeToAdd[1].outputs.objectRoot);
      this.modelNodeToAddSize = new THREE.Vector3();
      box.getSize(this.modelNodeToAddSize);

      // adding the cloned object to the tour
      // it will not be visible for the cursor
      node[0].obj3D.parent.add(this.modelNodeToAdd[0]);

      this.setNodeEditView(true, this.modelNodeToAdd[0]);

      this.iframeWindow.addEventListener('click', this.handleIframeClick, { once: true });
    } catch (e) {
      this.iframeWindow.removeEventListener('click', this.handleIframeClick);
    }
  };

  stopAddingProcess = async (node: any[]) => {
    this.iframeWindow.removeEventListener('click', this.handleIframeClick);

    // stopping the loader node
    if (this.loaderNode?.[0]) {
      this.loaderNode[0].stop();
    }

    this.iframeWindow.removeEventListener('click', this.handleIframeClick);
    this.inputComponent.inputs.userNavigationEnabled = true;
    this.inputComponent.inputs.eventsEnabled = false;
    this.inputComponent.inputs.unfiltered = true;

    node[0].obj3D.parent.remove(this.modelNodeToAdd?.[0]);
    node[0].stop();
    // this.setNodeEditView(false, node[0]);
    this.modelNodeToAdd = null;
    this.modelNodeToAddSize = null;
    this.loaderNode = null;
    // cleaning up the iframe click handler
    this.handleIframeClick = () => {};
  };

  handleModelIntersection = async (intersectionData: any) => {
    const THREE = await ThreeInstance.get();
    const mouse = new THREE.Vector3(
      intersectionData.position.x,
      intersectionData.position.y,
      intersectionData.position.z,
    );

    const normalizedCursor = new THREE.Vector3(
      intersectionData.normal.x,
      intersectionData.normal.y,
      intersectionData.normal.z,
    ).round();

    if (this.loaderNode?.length) {
      const halfX = this.loaderNode[1].inputs.size.x;
      // const halfY = this.orientedBoxComponent.inputs.size.y * 0.5;
      const halfZ = this.loaderNode[1].inputs.size.z;

      const mouseY =
        this.lastCreatedNodeType === ('image' || 'video') ? this.pose.position.y : mouse.y;

      // X
      if (normalizedCursor.x === 1 || normalizedCursor.x === -1) {
        this.loaderNode[0].obj3D.position.set(
          mouse.x + halfX * normalizedCursor.x,
          mouseY,
          mouse.z,
        );
      }

      // Y
      if (normalizedCursor.y === 1 || normalizedCursor.y === -1) {
        this.loaderNode[0].obj3D.position.set(mouse.x, mouseY, mouse.z);
      }

      // Z
      if (normalizedCursor.z === 1 || normalizedCursor.z === -1) {
        this.loaderNode[0].obj3D.position.set(
          mouse.x,
          mouseY,
          mouse.z + halfZ * normalizedCursor.z,
        );
      }
    }

    if (this.modelNodeToAdd?.length && this.modelNodeToAddSize) {
      const isMedia = isMediaType(this.modelNodeToAdd[1]);

      const mouseY = isMedia ? this.pose.position.y : mouse.y;
      if (isMedia) {
        this.modelNodeToAdd[0].rotation.set(0, degToRad(this.pose.rotation.y), 0);
      }

      const halfX = this.modelNodeToAddSize.x * 0.5;
      // const halfY = this.orientedBoxComponent.inputs.size.y * 0.5;
      const halfZ = this.modelNodeToAddSize.z * 0.5;

      // X
      if (normalizedCursor.x === 1 || normalizedCursor.x === -1) {
        this.modelNodeToAdd[0].position.set(mouse.x + halfX * normalizedCursor.x, mouseY, mouse.z);
      }

      // Y
      if (normalizedCursor.y === 1 || normalizedCursor.y === -1) {
        this.modelNodeToAdd[0].position.set(mouse.x, mouseY, mouse.z);
      }

      // Z
      if (normalizedCursor.z === 1 || normalizedCursor.z === -1) {
        this.modelNodeToAdd[0].position.set(mouse.x, mouseY, mouse.z + halfZ * normalizedCursor.z);
      }
    }
  };

  async initFloors(sweeps: any) {
    if (sweeps && this.config.frFloors?.length) {
      // using sweep.floor_position to get the floor height position
      sweeps.forEach((sweep: any) => {
        if (this.config.frFloors && !this.floors[sweep.floor_index]) {
          const frFloor = this.config.frFloors.find((i: any) => i.floor === sweep.floor_index);

          const floor = {
            floor: sweep.floor_index,
            position: sweep.floor_position,
          };
          this.floors[sweep.floor_index] = frFloor
            ? new FRFloor({
                ...frFloor,
                ...floor,
              })
            : new FRFloor(floor);
        }
        this.floors[sweep.floor_index].sweeps?.push(sweep);
      });
    }
  }

  moveTo = (data: any) => {
    const sweep = data.sweep_uuid || data.sweepUuid;
    if (!sweep) {
      throw new Error("Can't move to the object.");
    }
    if (!data.active) {
      throw new Error('Object is not active.');
    }

    this.mpSDK.Sweep.moveTo(sweep, {
      rotation: {
        x: parseFloat(data.srX) || 0,
        y: parseFloat(data.srY) || 0,
        z: parseFloat(data.srZ) || 0,
      },
      transition: this.mpSDK.Sweep.Transition.INSTANT,
      transitionTime: 900,
    });
  };

  //@ts-ignore TODO: move to utils
  setNodeEditView = (state = false, object: any) => {
    /** set state to true to render active component as a wireframe (green color for GLTF models) */

    if (!object) return;

    object.traverse((obj: THREE.Object3D) => {
      if (obj.type !== 'Mesh') return;
      // check if this is a single material or an array first, depends on the obj
    });
  };
}
